// Premium hotel-details page (OTA-standard).
//
// Hydration strategy — resilient against tab/session gotchas:
//   1. Fast path: localStorage preview (written by search card)
//   2. Content: `/api/v2/hotels/details/{id}` (HBX Content API — photos, description, amenities, coords)
//   3. Fallback: if no preview AND URL has search params, re-run `/api/v2/hotels/search`
//      and pick the matching hotel by id — this makes the page bookmark/deep-link safe.
// The page NEVER shows "unavailable" while at least one source is still loading.
// Only if every recovery lane fails do we surface an empty state.
//
// Phase A scope: real data only. Reviews, interactive maps, similar hotels — hidden.

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, MapPin, Star, Check, Image as ImageIcon, AlertTriangle, Heart, Bed,
  Wifi, Waves, Dumbbell, Utensils, Car, ShieldCheck, Wind, Coffee, Sparkles,
  ConciergeBell, Tv, Bath, X, Phone, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useCurrency } from '../../context/CurrencyContext';
import { useWishlist } from '../../lib/wishlist';
import { track, Events } from '../../lib/analytics';
import { recordHotelView } from '../../lib/recent';
import { api } from '../../lib/api';
import { loadHotelPreview, saveHotelPreview } from '../../lib/hotelCache';
import { RecentlyViewedHotels } from '../discovery/RecentRails';
import HotelDetailMap from './HotelDetailMap';
import HotelReviews from './HotelReviews';
import GuestsAlsoConsidered from './GuestsAlsoConsidered';
import NoImagePlaceholder from './NoImagePlaceholder';

// ETG meal-code → English label (pure translation of raw supplier codes).
const MEAL_LABELS = {
  breakfast: 'Breakfast included',
  'breakfast-buffet': 'Breakfast (buffet)',
  'half-board': 'Half board',
  'full-board': 'Full board',
  'all-inclusive': 'All inclusive',
  'ultra-all-inclusive': 'Ultra all-inclusive',
  'continental-breakfast': 'Continental breakfast',
  'american-breakfast': 'American breakfast',
  dinner: 'Dinner included',
};
const mealLabel = (raw) => {
  if (!raw) return '';
  const key = String(raw).toLowerCase();
  if (key === 'nomeal' || key === 'no-meal' || key === 'ro') return 'Room only';
  return MEAL_LABELS[key] || '';
};
const fmtCancelDeadline = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

// Amenity → lucide icon (keyword match, tolerates supplier variance).
const amenityIcon = (label = '') => {
  const l = String(label).toLowerCase();
  if (/wifi|wi-fi|internet/.test(l)) return Wifi;
  if (/pool|swim/.test(l)) return Waves;
  if (/gym|fitness/.test(l)) return Dumbbell;
  if (/restaurant|dining|breakfast|meal/.test(l)) return Utensils;
  if (/park|valet|car/.test(l)) return Car;
  if (/safe|security|24|reception/.test(l)) return ShieldCheck;
  if (/ac|air.?condition/.test(l)) return Wind;
  if (/coffee|tea|bar/.test(l)) return Coffee;
  if (/spa|sauna|wellness/.test(l)) return Sparkles;
  if (/concierge|butler|service/.test(l)) return ConciergeBell;
  if (/tv|television/.test(l)) return Tv;
  if (/bath|shower|toilet/.test(l)) return Bath;
  return Check;
};

// Small hook: scroll-spy for sticky sub-nav.
const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + 200;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [ids]);
  return active;
};

// Lightbox — full-screen gallery viewer.
const Lightbox = ({ images, index, onClose, onIndex }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex((index + 1) % images.length);
      if (e.key === 'ArrowLeft') onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, onClose, onIndex]);

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="hotel-lightbox">
      <div className="flex items-center justify-between p-4 text-white">
        <span className="text-sm font-medium">{index + 1} / {images.length}</span>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="hotel-lightbox-close">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 grid place-items-center px-4">
        <img
          src={images[index]}
          alt=""
          className="max-h-[80vh] max-w-full object-contain select-none"
        />
      </div>
      <div className="p-4 overflow-x-auto flex gap-2">
        {images.map((src, i) => (
          <button
            key={i}
            onClick={() => onIndex(i)}
            className={`flex-shrink-0 h-16 w-24 rounded-md overflow-hidden border-2 ${i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
          >
            <img src={src} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

// Merge helper — preview from search + backend content. Preview wins for rates,
// content wins for imagery/description/amenities (richer).
// Data-integrity rule: never invent fields. If ETG didn't return it, propagate
// the absence — the UI renders an empty state.
const mergeHotel = (preview, content) => {
  if (!preview && !content) return null;
  const contentImages = (content?.images || []).map((im) => (typeof im === 'string' ? im : im.url)).filter(Boolean);
  const previewImages = preview?.images || preview?.gallery || (preview?.image ? [preview.image] : []);
  const images = contentImages.length > 0 ? contentImages : previewImages;
  const contentAmenities = (content?.amenities || []).map((a) => (typeof a === 'string' ? a : a.name || a.description)).filter(Boolean);
  return {
    id: preview?.id || content?.hotel_code,
    name: preview?.name || content?.name,
    city: preview?.city || content?.city,
    area: preview?.area,
    address: preview?.address || content?.address,
    description: content?.description || preview?.description,
    rating: Number(preview?.rating ?? content?.rating ?? 0),
    reviews: Number(preview?.reviews ?? 0),
    category: content?.category || preview?.category,
    coordinates: content?.coordinates || preview?.coordinates,
    phones: content?.phones || [],
    images,
    amenities: contentAmenities.length > 0 ? contentAmenities : (preview?.amenities || []),
    // Rate / price info comes from the search snapshot only.
    room_type: preview?.room_type,
    board_name: preview?.board_name,
    meal: preview?.meal,
    rate_key: preview?.rate_key,
    rate_type: preview?.rate_type,
    net_price_per_night: preview?.net_price_per_night,
    display_price: preview?.display_price,
    price: preview?.price,
    original_price: preview?.original_price,
    currency: preview?.currency || 'INR',
    price_breakdown: preview?.price_breakdown,
    rates: preview?.rates,
    cancellation_policies: preview?.cancellation_policies,
    // Only true when ETG explicitly indicated free cancellation — never defaulted.
    has_free_cancellation: preview?.has_free_cancellation === true,
    free_cancellation_before: preview?.free_cancellation_before || null,
    rate_comments: preview?.rate_comments,
    promotions: preview?.promotions,
  };
};

const SectionCard = ({ id, title, icon: Icon, children, action }) => (
  <section id={id} className="scroll-mt-32">
    <Card>
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-red-600" />} {title}
          </h2>
          {action}
        </div>
        {children}
      </CardContent>
    </Card>
  </section>
);

const HotelDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { has, toggle } = useWishlist();

  // Search context re-hydrated from URL (bookmark-safe).
  const searchCtx = useMemo(() => ({
    city: searchParams.get('city') || '',
    lat: searchParams.get('lat') || '',
    lng: searchParams.get('lng') || '',
    check_in: searchParams.get('check_in') || '',
    check_out: searchParams.get('check_out') || '',
    rooms: parseInt(searchParams.get('rooms'), 10) || 1,
    guests: parseInt(searchParams.get('guests'), 10) || 2,
    children: parseInt(searchParams.get('children'), 10) || 0,
    child_ages: searchParams.get('child_ages') || '',
  }), [searchParams]);

  const nights = useMemo(() => {
    const ci = new Date(searchCtx.check_in);
    const co = new Date(searchCtx.check_out);
    if (Number.isNaN(ci.getTime()) || Number.isNaN(co.getTime())) return 1;
    return Math.max(1, Math.round((co - ci) / (1000 * 60 * 60 * 24)));
  }, [searchCtx.check_in, searchCtx.check_out]);

  // Fast path: localStorage preview.
  const [preview, setPreview] = useState(() => loadHotelPreview(id));
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedRateIdx, setSelectedRateIdx] = useState(0);
  const [reviewsAvailable, setReviewsAvailable] = useState(false);

  // Fetch backend content (Content API — always try, source of truth for imagery/text).
  useEffect(() => {
    let cancelled = false;
    setContentLoading(true);
    setContent(null);
    (async () => {
      try {
        const { data } = await api.get(`/v2/hotels/details/${id}`);
        if (!cancelled) setContent(data?.content || null);
      } catch {
        // 404 is expected for non-HBX hotels — the preview / search fallback covers it.
      } finally {
        if (!cancelled) setContentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Recovery: if we have NO preview but we DO have search params → re-run search.
  useEffect(() => {
    if (preview || recoveryLoading) return;
    if (!searchCtx.city || !searchCtx.check_in || !searchCtx.check_out) return;
    let cancelled = false;
    setRecoveryLoading(true);
    (async () => {
      try {
        const { data } = await api.post('/v2/hotels/search', {
          city: searchCtx.city,
          lat: searchCtx.lat ? parseFloat(searchCtx.lat) : null,
          lng: searchCtx.lng ? parseFloat(searchCtx.lng) : null,
          check_in: searchCtx.check_in,
          check_out: searchCtx.check_out,
          rooms: searchCtx.rooms,
          guests: searchCtx.guests,
          children: searchCtx.children,
          child_ages: String(searchCtx.child_ages || '').split(',').map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n)),
          nights,
          tier: 'b2c',
          use_provider: true,
        });
        if (cancelled) return;
        const match = (data?.hotels || []).find((h) => String(h.id) === String(id));
        if (match) {
          setPreview(match);
          saveHotelPreview(id, match);
        }
      } catch { /* recovery lane silently fails, backend content may still render */ }
      finally { if (!cancelled) setRecoveryLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, preview, searchCtx, nights]);

  const hotel = useMemo(() => mergeHotel(preview, content), [preview, content]);

  // Decide "not found" ONLY after both lanes have finished and there's still nothing.
  useEffect(() => {
    if (!contentLoading && !recoveryLoading && !preview && !content) {
      setNotFound(true);
    }
  }, [contentLoading, recoveryLoading, preview, content]);

  // Analytics — fire once we know the hotel.
  useEffect(() => {
    if (!hotel?.id) return;
    track(Events.HOTEL_VIEWED, { hotel_id: hotel.id, city: hotel.city });
    recordHotelView({
      id: hotel.id,
      name: hotel.name,
      image: hotel.images?.[0],
      city: hotel.city,
      area: hotel.area,
      star_rating: hotel.category,
      review_rating: hotel.rating,
      google_rating: hotel.google_rating,
      price: hotel.net_price_per_night ?? hotel.display_price ?? hotel.price,
      currency: hotel.currency,
      check_in: searchCtx.check_in,
      check_out: searchCtx.check_out,
      rooms: searchCtx.rooms,
      guests: searchCtx.guests,
    });
  }, [hotel?.id, hotel?.city, hotel?.name, hotel?.area, hotel?.rating, hotel?.category, hotel?.currency, hotel?.images, hotel?.display_price, hotel?.net_price_per_night, hotel?.price, hotel?.google_rating, searchCtx.check_in, searchCtx.check_out, searchCtx.rooms, searchCtx.guests]);

  const rates = useMemo(() => {
    if (!hotel) return [];
    if (hotel.rates && hotel.rates.length > 0) return hotel.rates;
    if (hotel.net_price_per_night) {
      return [{
        rate_key: hotel.rate_key,
        rate_type: hotel.rate_type,
        room_name: hotel.room_type,
        meal: hotel.meal || hotel.board_name,
        board_name: hotel.meal || hotel.board_name,
        net: hotel.net_price_per_night,
        selling_rate: hotel.display_price ?? hotel.price ?? hotel.net_price_per_night,
        selling_total: (hotel.display_price ?? hotel.price ?? hotel.net_price_per_night) * (Math.max(1, nights)),
        net_total: hotel.net_price_per_night * Math.max(1, nights),
        has_free_cancellation: hotel.has_free_cancellation === true,
        free_cancellation_before: hotel.free_cancellation_before || null,
        cancellation_policies: hotel.cancellation_policies,
      }];
    }
    return [];
  }, [hotel, nights]);

  // Reset room-selection when the rates list changes (e.g. re-hydration).
  useEffect(() => {
    if (selectedRateIdx >= rates.length) setSelectedRateIdx(0);
  }, [rates.length, selectedRateIdx]);

  // The currently-selected rate — this drives the entire booking summary.
  const selectedRate = rates[selectedRateIdx] || rates[0] || null;

  const sections = useMemo(() => {
    const s = ['overview'];
    if (rates.length > 0) s.push('rooms');
    if (hotel?.amenities?.length > 0) s.push('amenities');
    if (hotel?.coordinates?.latitude) s.push('location');
    if (reviewsAvailable) s.push('reviews');
    if (selectedRate?.cancellation_policies?.length > 0 || hotel?.rate_comments) s.push('policies');
    return s;
  }, [rates.length, hotel?.amenities?.length, hotel?.coordinates?.latitude, reviewsAvailable, selectedRate?.cancellation_policies?.length, hotel?.rate_comments]);

  const activeSection = useActiveSection(sections);

  const scrollToSection = useCallback((sid) => {
    const el = document.getElementById(sid);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const proceed = () => {
    if (!hotel || !selectedRate) return;
    track(Events.ROOM_SELECTED, { hotel_id: hotel.id, rate_key: selectedRate.rate_key, meal: selectedRate.meal });
    track(Events.BOOKING_STARTED, { service: 'hotels', hotel_id: hotel.id });
    // The FULL selected-rate object is forwarded so the checkout page uses
    // exactly the rate/price the user saw at selection time. Never re-derive
    // from the hotel's cheapest rate.
    navigate(`/booking/hotels/${hotel.id}`, {
      state: {
        item: {
          ...hotel,
          // Overwrite hotel-level pricing with the SELECTED rate — this is
          // what the sidebar summary + prebook/confirm payloads must use.
          rate_key: selectedRate.rate_key || hotel.rate_key,
          book_hash: selectedRate.book_hash,
          match_hash: selectedRate.match_hash,
          room_type: selectedRate.room_name || hotel.room_type,
          meal: selectedRate.meal,
          board_name: selectedRate.meal || selectedRate.board_name,
          net_price_per_night: selectedRate.net,
          display_price: selectedRate.selling_rate,
          selling_total: selectedRate.selling_total,
          net_total: selectedRate.net_total,
          has_free_cancellation: selectedRate.has_free_cancellation === true,
          free_cancellation_before: selectedRate.free_cancellation_before,
          cancellation_policies: selectedRate.cancellation_policies,
          selected_rate: selectedRate,   // full snapshot for prebook
          // Search context — the checkout wizard uses these for the summary.
          check_in: searchCtx.check_in,
          check_out: searchCtx.check_out,
          rooms: searchCtx.rooms,
          guests: searchCtx.guests,
          __nights: nights,
        },
      },
    });
  };

  // ————————————————————— render lanes —————————————————————
  const loading = (contentLoading || recoveryLoading) && !hotel;

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950">
        <div className="flex flex-col items-center gap-3 text-gray-600 dark:text-gray-400">
          <Loader2 className="h-8 w-8 animate-spin text-red-600" />
          <p className="text-sm">Loading hotel details…</p>
        </div>
      </div>
    );
  }

  if (notFound || !hotel) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center" data-testid="hotel-detail-notfound">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">We couldn&apos;t load this hotel</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          The hotel may no longer be available. Please run a new search — we&apos;ll show you fresh live options.
        </p>
        <Link to="/hotels/search" className="inline-flex items-center gap-1 text-red-600 hover:underline">
          <ChevronLeft className="h-4 w-4" /> Back to hotel search
        </Link>
      </div>
    );
  }

  const gallery = hotel.images || [];
  const wishlisted = has(hotel.id);
  // The sidebar total must recompute from the SELECTED rate every time the
  // user picks a different room. Never use the hotel-level cheapest.
  const perNight = Number(selectedRate?.selling_rate ?? selectedRate?.net ?? hotel.net_price_per_night ?? 0);
  const totalPrice = Number(
    selectedRate?.selling_total
    ?? (selectedRate?.selling_rate ? selectedRate.selling_rate * nights : null)
    ?? hotel.display_price
    ?? (hotel.net_price_per_night ? hotel.net_price_per_night * nights : 0),
  );
  const netTotal = Number(selectedRate?.net_total ?? (selectedRate?.net ? selectedRate.net * nights : (hotel.net_price_per_night ? hotel.net_price_per_night * nights : 0)));
  const markupAmount = Math.max(0, totalPrice - netTotal);
  const stayLabel = searchCtx.check_in && searchCtx.check_out
    ? `${searchCtx.check_in} → ${searchCtx.check_out} · ${nights} night${nights > 1 ? 's' : ''} · ${searchCtx.rooms} room${searchCtx.rooms > 1 ? 's' : ''}, ${searchCtx.guests} guest${searchCtx.guests > 1 ? 's' : ''}`
    : `${nights} night${nights > 1 ? 's' : ''}`;

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen pb-24">
      {lightboxOpen && gallery.length > 0 && (
        <Lightbox
          images={gallery}
          index={activeImage}
          onClose={() => setLightboxOpen(false)}
          onIndex={setActiveImage}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-red-600 mb-3"
          data-testid="hotel-detail-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back to results
        </button>

        {/* Title row */}
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1
              className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight"
              data-testid="hotel-detail-title"
            >
              {hotel.name}
            </h1>
            <div className="mt-1 flex items-center gap-3 flex-wrap text-sm">
              {hotel.category && (
                <div className="flex items-center gap-0.5" data-testid="hotel-detail-stars">
                  {Array.from({ length: Math.max(0, Math.min(5, Math.round(Number(hotel.category)))) }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
              )}
              <p className="text-gray-600 dark:text-gray-400 flex items-center gap-1 min-w-0">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{[hotel.address, hotel.city].filter(Boolean).join(', ')}</span>
              </p>
              {hotel.phones?.[0] && (
                <a href={`tel:${hotel.phones[0]}`} className="hidden sm:inline-flex items-center gap-1 text-gray-500 hover:text-red-600">
                  <Phone className="h-3.5 w-3.5" /> {hotel.phones[0]}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hotel.rating > 0 && (
              <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 rounded font-bold text-sm">
                {Number(hotel.rating).toFixed(1)} <Star className="h-3.5 w-3.5 fill-current" />
              </div>
            )}
            <button
              onClick={() => toggle(hotel.id)}
              data-testid="hotel-detail-wishlist"
              className={`p-2 rounded-full border transition ${wishlisted ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 dark:border-gray-700 text-gray-600 hover:border-red-300'}`}
              aria-label="Toggle wishlist"
            >
              <Heart className={`h-4 w-4 ${wishlisted ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>

        {/* Hero gallery — real supplier images only. When the supplier
            didn't return images we render a single "No image available"
            block instead of substituting stock photos. */}
        {gallery.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-2 mb-6" data-testid="hotel-detail-gallery">
            <button
              onClick={() => { setActiveImage(0); setLightboxOpen(true); }}
              className="relative rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 h-80 md:h-[420px] group"
              data-testid="hotel-gallery-main"
            >
              <img src={gallery[0]} alt={hotel.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded">
                <ImageIcon className="h-3 w-3" /> {gallery.length} photo{gallery.length > 1 ? 's' : ''}
              </div>
            </button>
            <div className="grid grid-cols-2 gap-2">
              {gallery.slice(1, 5).map((src, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveImage(i + 1); setLightboxOpen(true); }}
                  className="relative h-[100px] md:h-[206px] rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 group"
                  data-testid={`hotel-gallery-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {i === 3 && gallery.length > 5 && (
                    <div className="absolute inset-0 bg-black/60 grid place-items-center text-white font-bold text-sm">
                      +{gallery.length - 5} photos
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="rounded-2xl overflow-hidden h-72 md:h-[380px] mb-6"
            data-testid="hotel-detail-gallery-empty"
          >
            <NoImagePlaceholder label="No images available from supplier" />
          </div>
        )}

        {/* Sticky sub-nav */}
        {sections.length > 1 && (
          <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 mb-6">
            <nav className="flex items-center gap-1 overflow-x-auto py-2" data-testid="hotel-detail-nav">
              {sections.map((s) => (
                <button
                  key={s}
                  onClick={() => scrollToSection(s)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition ${
                    activeSection === s
                      ? 'bg-red-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  data-testid={`hotel-detail-nav-${s}`}
                >
                  {s}
                </button>
              ))}
            </nav>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* MAIN COLUMN */}
          <div className="space-y-6 min-w-0">
            <SectionCard id="overview" title="About this property" icon={ImageIcon}>
              {hotel.description ? (
                <div
                  className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                  data-testid="hotel-detail-description"
                  // HBX ships sanitized HTML descriptions.
                  dangerouslySetInnerHTML={{ __html: hotel.description }}
                />
              ) : (
                <p
                  className="text-sm text-gray-500 dark:text-gray-400 italic"
                  data-testid="hotel-detail-description-empty"
                >
                  Property description not available from supplier.
                </p>
              )}
            </SectionCard>

            {rates.length > 0 && (
              <SectionCard id="rooms" title="Select your room" icon={Bed}>
                <div className="space-y-2" data-testid="rate-options">
                  {rates.map((r, idx) => {
                    const selected = selectedRateIdx === idx;
                    const meal = mealLabel(r.meal || r.board_name || '');
                    const rateFreeCancel = r.has_free_cancellation === true && !!r.free_cancellation_before;
                    return (
                      <label
                        key={idx}
                        className={`block p-4 rounded-xl border cursor-pointer transition ${
                          selected
                            ? 'border-red-500 ring-1 ring-red-500 bg-red-50/50 dark:bg-red-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-red-300 hover:shadow-sm'
                        }`}
                      >
                        <input
                          type="radio"
                          name="rate"
                          className="sr-only"
                          checked={selected}
                          onChange={() => setSelectedRateIdx(idx)}
                          data-testid={`rate-${idx}`}
                        />
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                              {r.room_name || hotel.room_type || 'Room'}
                            </p>
                            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-600 dark:text-gray-400">
                              {meal && <span>{meal}</span>}
                              {rateFreeCancel && (
                                <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-medium">
                                  <Check className="h-3 w-3" /> Free cancellation until {fmtCancelDeadline(r.free_cancellation_before)}
                                </span>
                              )}
                              {r.rate_type === 'RECHECK' && (
                                <Badge variant="secondary" className="text-[10px] py-0">Price verified at checkout</Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-red-600" data-testid={`rate-price-${idx}`}>
                              {formatPrice(r.selling_rate || r.net || 0)}
                            </p>
                            <p className="text-[11px] text-gray-500">per night</p>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {hotel.amenities?.length > 0 && (
              <SectionCard id="amenities" title="Facilities & amenities" icon={Sparkles}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                  {hotel.amenities.map((a, i) => {
                    const Icon = amenityIcon(a);
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 min-w-0">
                        <Icon className="h-4 w-4 text-red-600 flex-shrink-0" />
                        <span className="truncate">{a}</span>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {hotel.coordinates?.latitude && (
              <SectionCard id="location" title="Location & neighbourhood" icon={MapPin}>
                <HotelDetailMap hotel={hotel} />
              </SectionCard>
            )}

            {/* Reviews — auto-hides via onAvailable when Places API is off / no match. */}
            <div id="reviews" className={reviewsAvailable ? 'scroll-mt-32' : 'hidden'}>
              <SectionCard id="reviews-inner" title="Guest reviews" icon={Star}>
                <HotelReviews hotel={hotel} onAvailable={setReviewsAvailable} />
              </SectionCard>
            </div>

            {(selectedRate?.cancellation_policies?.length > 0 || hotel.rate_comments || hotel.promotions?.length > 0) && (
              <SectionCard id="policies" title="Policies & rate info" icon={AlertTriangle}>
                {selectedRate?.free_cancellation_before && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300 mb-1 uppercase tracking-wider">Free cancellation</p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 flex-shrink-0" />
                      Cancel free of charge until {fmtCancelDeadline(selectedRate.free_cancellation_before)}
                    </p>
                  </div>
                )}
                {selectedRate?.cancellation_policies?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-gray-800 dark:text-gray-200 mb-1 uppercase tracking-wider">Cancellation policy</p>
                    <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
                      {selectedRate.cancellation_policies.map((p, i) => {
                        const amt = Number(p.amount || 0);
                        const from = p.from ? fmtCancelDeadline(p.from) : null;
                        const until = p.until ? fmtCancelDeadline(p.until) : null;
                        if (amt === 0) {
                          return (
                            <li key={i} className="flex items-start gap-2">
                              <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <span>No cancellation fee{until ? ` until ${until}` : ''}.</span>
                            </li>
                          );
                        }
                        return (
                          <li key={i} className="flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span>
                              {formatPrice(amt)} cancellation fee
                              {from ? ` from ${from}` : ''}
                              {until ? ` until ${until}` : ''}
                              .
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {hotel.rate_comments && (
                  <div className="mb-3">
                    <p className="text-xs font-bold text-gray-800 dark:text-gray-200 mb-1 uppercase tracking-wider">Rate comments</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{String(hotel.rate_comments)}</p>
                  </div>
                )}
                {hotel.promotions?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-1 uppercase tracking-wider">Promotions</p>
                    <ul className="text-sm text-emerald-700 dark:text-emerald-400 space-y-1">
                      {hotel.promotions.map((p, i) => <li key={i}>• {p.name || String(p)}</li>)}
                    </ul>
                  </div>
                )}
              </SectionCard>
            )}
          </div>

          {/* STICKY BOOKING SIDEBAR */}
          <aside className="lg:sticky lg:top-24 self-start space-y-3">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Your stay</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{stayLabel}</p>

                <div className="border-t border-gray-200 dark:border-gray-800 pt-3">
                  <div className="space-y-1.5 text-sm" data-testid="hotel-detail-breakdown">
                    {selectedRate?.room_name && (
                      <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">
                        {selectedRate.room_name}
                        {mealLabel(selectedRate.meal) ? ` · ${mealLabel(selectedRate.meal)}` : ''}
                      </p>
                    )}
                    {perNight > 0 && (
                      <div className="flex justify-between text-gray-700 dark:text-gray-300">
                        <span>{formatPrice(perNight)} × {nights} night{nights > 1 ? 's' : ''}</span>
                        <span>{formatPrice(perNight * nights)}</span>
                      </div>
                    )}
                    {markupAmount > 0 && netTotal > 0 && (
                      <div className="flex justify-between text-gray-500 text-xs">
                        <span>Service fee &amp; taxes</span>
                        <span>{formatPrice(markupAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-800 mt-2">
                      <span>Total</span>
                      <span data-testid="hotel-detail-price">{formatPrice(totalPrice)}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 text-right">incl. taxes &amp; fees</p>
                  </div>
                </div>

                <Button
                  onClick={proceed}
                  data-testid="hotel-detail-book"
                  disabled={rates.length === 0}
                  className="w-full bg-red-600 hover:bg-red-700 text-white mt-4 h-11 text-sm font-semibold disabled:opacity-50"
                >
                  {rates.length === 0 ? 'Rates unavailable' : 'Book Now'}
                </Button>
                {selectedRate?.has_free_cancellation && selectedRate.free_cancellation_before && (
                  <p className="text-[11px] text-emerald-700 dark:text-emerald-400 text-center mt-2 flex items-center justify-center gap-1">
                    <Check className="h-3 w-3" /> Free cancellation until {fmtCancelDeadline(selectedRate.free_cancellation_before)}
                  </p>
                )}
                <p className="text-[11px] text-gray-500 text-center mt-2 flex items-center justify-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Secure booking
                </p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>

      {/* Recently viewed rail — always at the bottom above GAC. */}
      <RecentlyViewedHotels variant="detail" />

      {/* Guests Also Considered — anonymized aggregated recs. Falls back to
          similarity when signals are thin, so the rail always renders when
          matching hotels exist. */}
      <GuestsAlsoConsidered hotel={hotel} searchCtx={searchCtx} nights={nights} />
    </div>
  );
};

export default HotelDetailPage;
